import { useRef, useState, useEffect, memo, useLayoutEffect } from "react";
import { Cont, Control, ControlCont, Segment } from "./styles";
import { Tooltip } from "react-tooltip";
import { Strong } from "../Typeography";
import { Link } from "react-router-dom";

const SegmentedControl = ({
  name,
  segments,
  callback,
  defaultIndex = 0,
}: {
  name: string
  segments: {
    value: string
    label: string
    ref: React.RefObject<HTMLDivElement>
    link?: string
    tooltip?: {
      label: string
      id: string
      defaultIsOpen?: boolean
    }
  }[]
  callback?: (value: string, index: number) => void
  defaultIndex?: number
}) => {
  const [activeIndex, setActiveIndex] = useState(defaultIndex ?? 0);
  const [offset, setOffset] = useState({ width: 0, xPos: 0 });
  const isMounted = useRef<boolean>(false);

  const calculateOffset = () => {
    if (!activeSegmentRef?.current) return;
    const { offsetWidth, offsetLeft } = activeSegmentRef.current;
    setOffset({ width: offsetWidth, xPos: offsetLeft });
  };
  
  useEffect(() => {
    isMounted.current = true;
    calculateOffset();
  }, []);

  const onInputChange = (value, index) => {
    setActiveIndex(index);
    if (callback) callback(value, index);
  }
  
  const activeSegmentRef = segments[activeIndex]?.ref;
  useLayoutEffect(() => {
    calculateOffset();
  }, [activeSegmentRef, isMounted.current]);

  useLayoutEffect(() => {
    const handleResize = () => calculateOffset();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Cont>
      <ControlCont>
        <Control
          isMounted={isMounted.current}
          highlightWidth={offset.width}
          highlightXPos={offset.xPos}
        >
          {segments?.map((item, i) => {
            const withLink = !!item.link
            const content = (
              <Segment
                key={item.value}
                isActive={i === activeIndex}
                ref={item.ref}
              >
                <input
                  type="radio"
                  value={item.value}
                  id={item.label}
                  name={name}
                  onChange={() => onInputChange(item.value, i)}
                  checked={i === activeIndex}
                />
                <label htmlFor={item.label} data-tooltip-id={item.tooltip?.id}>{item.label}</label>
              </Segment>
            )
            if (withLink) {
              return (
                <Link to={item.link} key={item.value}>
                  {content}
                </Link>
              )
            }
            return content
          })}
        </Control>
      </ControlCont>
      {segments?.map((item, i) =>
        item.tooltip ? (
          <Tooltip
            key={item.value}
            id={item.tooltip.id}
            defaultIsOpen={item.tooltip.defaultIsOpen}
            opacity={1}
            place="bottom"
            offset={16}
            style={{
              zIndex: 1,
              background: 'var(--surface-brand)',
              borderRadius: 'var(--units-8, 8px)',
              color: 'var(--text-invert)',
            }}
            border="1px solid var(--stroke-invert, #080808)"
          >
            <Strong s="M" c="text-invert" fw="semibold" ls="-0.28px">{item.tooltip.label}</Strong>
          </Tooltip>
        ) : null
      )}
    </Cont>
  );
};

export default memo(SegmentedControl);
